import { useState, useEffect, useCallback } from 'react'
import { Package, Truck, MapPin } from 'lucide-react'
import OrdersTab from '@/components/shipping/OrdersTab'
import ShippingQueueTab from '@/components/shipping/ShippingQueueTab'
import TrackingTab from '@/components/shipping/TrackingTab'
import { ShippingProvider, useShippingContext } from '@/contexts/ShippingContext'

// Tipos de pestañas
type TabType = 'orders' | 'queue' | 'tracking'

interface TabConfig {
  id: TabType
  label: string
  icon: React.ElementType
  description: string
}

const TABS: TabConfig[] = [
  {
    id: 'orders',
    label: 'Pedidos',
    icon: Package,
    description: 'Pedidos entrantes de PrestaShop'
  },
  {
    id: 'queue',
    label: 'Cola de envío',
    icon: Truck,
    description: 'Preparar y generar etiquetas'
  },
  {
    id: 'tracking',
    label: 'Seguimiento',
    icon: MapPin,
    description: 'Tracking de envíos'
  }
]

// Componente interno que usa el contexto
function ShippingPageContent() {
  const [activeTab, setActiveTab] = useState<TabType>('orders')
  const { setNavigateToTracking } = useShippingContext()

  // Registrar el callback de navegación al contexto
  const navigateToTracking = useCallback(() => {
    setActiveTab('tracking')
  }, [])

  useEffect(() => {
    setNavigateToTracking(() => navigateToTracking)
    return () => setNavigateToTracking(null)
  }, [setNavigateToTracking, navigateToTracking])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header con tabs estilo Outvio */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="px-6">
          {/* Tabs */}
          <div className="flex items-center gap-8 pt-4">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-4 border-b-2 transition-all ${
                    isActive
                      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-teal-500' : ''}`} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Contenido de cada tab */}
      <div className="h-[calc(100vh-65px)]">
        {activeTab === 'orders' && <OrdersTab onNavigateToQueue={() => setActiveTab('queue')} />}
        {activeTab === 'queue' && <ShippingQueueTab />}
        {activeTab === 'tracking' && <TrackingTab />}
      </div>
    </div>
  )
}

export default function ShippingPage() {
  return (
    <ShippingProvider>
      <ShippingPageContent />
    </ShippingProvider>
  )
}
